import { cn } from '@1trading/react';

// Levels 0 and 1 start expanded; deeper nodes start collapsed
const EXPANDED_DEPTH = 2;

const VALUE_CLASSES: Record<string, string> = {
  string: 'text-emerald-600 dark:text-emerald-400',
  number: 'text-amber-600 dark:text-amber-400',
  boolean: 'text-violet-600 dark:text-violet-400',
};

interface JsonTreeProps {
  data: unknown;
  label?: string;
  depth?: number;
}

/**
 * Collapsible pretty-printed JSON tree (replaces legacy vue-json-pretty).
 * Uses native <details> so every object/array node can be folded.
 */
export const JsonTree = ({ data, label, depth = 0 }: JsonTreeProps) => {
  if (typeof data === 'object' && data !== null) {
    const entries = Array.isArray(data) ? data.map((value, index) => [String(index), value] as const) : Object.entries(data as Record<string, unknown>);
    const preview = Array.isArray(data) ? `[${entries.length}]` : `{${entries.length}}`;
    return (
      <details open={depth < EXPANDED_DEPTH}>
        <summary className="cursor-pointer select-none">
          {label === undefined ? null : <span className="text-sky-700 dark:text-sky-400">{label}</span>}
          <span className="text-muted-foreground"> {preview}</span>
        </summary>
        <div className="ml-1.5 flex flex-col border-border border-l pl-3">
          {entries.length === 0 ? (
            <span className="text-muted-foreground italic">empty</span>
          ) : (
            entries.map(([entryKey, value]) => <JsonTree data={value} depth={depth + 1} key={entryKey} label={entryKey} />)
          )}
        </div>
      </details>
    );
  }
  return (
    <div className="break-all">
      {label === undefined ? null : <span className="text-sky-700 dark:text-sky-400">{label}: </span>}
      <span className={cn(VALUE_CLASSES[typeof data])}>{JSON.stringify(data) ?? 'undefined'}</span>
    </div>
  );
};
JsonTree.displayName = 'JsonTree';

/** `JsonTree` in a card, which scrolls sideways when a value is too long to wrap */
export const JsonTreeCard = ({ data }: { data: unknown }) => (
  <div className="overflow-x-auto rounded-lg border bg-card p-4 text-sm">
    <JsonTree data={data} />
  </div>
);
JsonTreeCard.displayName = 'JsonTreeCard';
